import type { CoResidentForDropdown } from '../../types/modals'
import type { PaymentConfig } from '@/types/payment'
import {
  computeExternalContractAdjustmentAmount,
  formatExternalPaymentSurchargeFee,
  getCertificateSurchargeFee,
} from '@/lib/utils/paymentCalculations'
import { formatCurrencyInput } from '@/lib/utils/formatters'

export const EXTERNAL_APPLICATION_FEE_AMOUNT = 35

type PaymentHistoryRow = Record<string, unknown>

/** Angular `paymentCalculate` / `payment-refund` CONTRACT completed-payment sum. */
export function computeExternalContractStartingBalance(
  history: PaymentHistoryRow[] | undefined,
): number {
  if (!history?.length) return 0
  let total = 0
  for (const row of history) {
    const rowType = String(row?.payment_type ?? '')
    const rowStatus = String(row?.payment_status ?? '')
    if (rowStatus !== 'Completed' || !rowType.includes('CONTRACT')) continue

    const amount = Number(row?.payment_amount ?? 0) || 0
    const surcharge = Number(row?.payment_log_surcharge_fee ?? 0) || 0
    const refundFee = Number(row?.payment_log_refund_fee ?? 0) || 0

    if (surcharge || refundFee) {
      if (surcharge) total += amount - surcharge
      if (refundFee) total += amount + -1 * refundFee
    } else {
      total += amount
    }
  }
  return total
}

/** Angular APPLICATION_FEE external payment starting balance (per applicant). */
export function computeExternalApplicationFeeStartingBalance(
  history: PaymentHistoryRow[] | undefined,
  applicantId: string,
): number {
  if (!history?.length) return 0
  let total = 0
  for (const row of history) {
    const rowType = String(row?.payment_type ?? '')
    const rowStatus = String(row?.payment_status ?? '')
    if (rowStatus !== 'Completed' || !rowType.includes('APPLICATION_FEE')) continue
    if (String(row?.fk_applicant_id ?? '') !== String(applicantId)) continue
    total += Number(row?.payment_amount ?? 0) || 0
  }
  return total
}

export function resolveExternalPaymentAccessFee(
  applicationAllData: Record<string, unknown> | undefined,
): number {
  return Number(applicationAllData?.access_fee ?? 0) || 0
}

export function resolveExternalPaymentBalanceDue(
  applicationAllData: Record<string, unknown> | undefined,
): number {
  return Number(applicationAllData?.balance_due ?? 0) || 0
}

/** Angular PaymentRefundComponent coresidentLists for external payment. */
export function buildExternalPaymentCoResidentList(
  applicationAllData: Record<string, unknown> | undefined,
  paymentType: string,
  applicantId: string,
): CoResidentForDropdown[] {
  const map = applicationAllData?.co_resident_application as
    | Record<
        string,
        {
          applicant_status?: string
          user_data?: {
            fk_user_id?: string | number
            first_name?: string
            last_name?: string
          }
        }
      >
    | undefined
  if (!map) return []

  const toEntry = (user?: {
    fk_user_id?: string | number
    first_name?: string
    last_name?: string
  }): CoResidentForDropdown | null => {
    const uid = user?.fk_user_id
    if (uid === undefined || uid === null || uid === '') return null
    return {
      fk_user_id: uid,
      first_name: user?.first_name,
      last_name: user?.last_name,
    }
  }

  if (paymentType === 'APPLICATION_FEE') {
    const current = map[applicantId]?.user_data
    const entry = toEntry(current)
    return entry ? [entry] : []
  }

  const approved: CoResidentForDropdown[] = []
  for (const resident of Object.values(map)) {
    if (resident?.applicant_status !== 'APPROVED') continue
    const entry = toEntry(resident.user_data)
    if (entry) approved.push(entry)
  }

  if (approved.length > 0) return approved

  const fallback = toEntry(map[applicantId]?.user_data)
  return fallback ? [fallback] : []
}

export function formatExternalPaymentAdjustmentAmount(amount: number): string {
  if (!Number.isFinite(amount)) return ''
  if (amount === 0) return formatCurrencyInput(0, true)
  if (amount < 0) {
    const formatted = formatCurrencyInput(Math.abs(amount), true)
    return formatted ? `-${formatted}` : formatCurrencyInput(0, true)
  }
  return formatCurrencyInput(amount, true)
}

export function parseSignedCalculatorAmount(value: string): number {
  const trimmed = value.trim()
  if (!trimmed) return 0
  const negative = trimmed.startsWith('-') || trimmed.includes('($')
  const numeric = parseFloat(trimmed.replace(/[^0-9.]/g, '')) || 0
  return negative ? -numeric : numeric
}

export function parseCalculatorAmount(value: string): number {
  return parseFloat(value.replace(/[^0-9.]/g, '')) || 0
}

/** Angular `paymentCalculate('external_payment')` + `calculateFee()`. */
export function computeExternalPaymentFormAmounts(params: {
  isApplicationFee: boolean
  accessFee: number
  startingBalance: number
  paymentConfig: PaymentConfig | Record<string, unknown> | null
  paymentSource: string
}) {
  const { isApplicationFee, accessFee, startingBalance, paymentConfig, paymentSource } =
    params

  if (isApplicationFee) {
    return {
      adjustmentAmount: formatExternalPaymentAdjustmentAmount(
        EXTERNAL_APPLICATION_FEE_AMOUNT,
      ),
      surchargeAmount: '',
    }
  }

  const adjustment = computeExternalContractAdjustmentAmount(
    accessFee,
    startingBalance,
  )
  const surcharge = getCertificateSurchargeFee(paymentConfig, paymentSource)

  return {
    adjustmentAmount: formatExternalPaymentAdjustmentAmount(adjustment),
    surchargeAmount: formatExternalPaymentSurchargeFee(surcharge),
  }
}

/** Angular `paymentCalculation` for external_payment (+ `updateBalance` when edited). */
export function computeExternalPaymentCalculatorDisplay(params: {
  isApplicationFee: boolean
  balanceDue: number
  startingBalance: number
  adjustmentPayment: number
  surchargePayment: number
  calculatorManuallyEdited: boolean
}) {
  const {
    isApplicationFee,
    balanceDue,
    startingBalance,
    adjustmentPayment,
    surchargePayment,
    calculatorManuallyEdited,
  } = params

  if (isApplicationFee) {
    return {
      accountBalance: startingBalance,
      refundAmount: 0,
      balanceOwed: 0,
      netAmountDue: EXTERNAL_APPLICATION_FEE_AMOUNT,
    }
  }

  const accountBalance = calculatorManuallyEdited
    ? startingBalance + adjustmentPayment
    : startingBalance

  return {
    accountBalance,
    refundAmount: 0,
    balanceOwed: balanceDue - adjustmentPayment,
    netAmountDue: computeExternalPaymentNetAmountDue(
      adjustmentPayment,
      surchargePayment,
      false,
    ),
  }
}

export function computeExternalPaymentNetAmountDue(
  adjustmentPayment: number,
  surchargePayment: number,
  isApplicationFee: boolean,
): number {
  if (isApplicationFee) return EXTERNAL_APPLICATION_FEE_AMOUNT
  return adjustmentPayment + surchargePayment
}

/** Angular `isExternalPayment && !pa_id` submit body. */
export function buildExternalPaymentSubmitPayload(params: {
  applicantId: string
  paymentType: string
  adjustmentPayment: number
  surchargePayment: number
  paymentSource: string
  paymentMethod: string
  checkNumber: string
  transactionId: string
  certificatePaidBy: string
  covid19: boolean
  transactionNotes: string
  isContract: boolean
  isPaperCheck: boolean
}) {
  const {
    paymentType,
    adjustmentPayment,
    surchargePayment,
    paymentSource,
    paymentMethod,
    checkNumber,
    transactionId,
    certificatePaidBy,
    covid19,
    transactionNotes,
    isContract,
    isPaperCheck,
  } = params

  const refundAmount = adjustmentPayment + (isContract ? surchargePayment : 0)

  return {
    adjustment_type: 'external_payment' as const,
    payment_type: paymentType,
    payment_amount: adjustmentPayment.toFixed(2),
    refund_amount: refundAmount.toFixed(2),
    payment_source: paymentSource,
    payment_method: paymentMethod,
    ...(isPaperCheck ? { check_number: checkNumber } : {}),
    ...(transactionId ? { transaction_id: transactionId } : {}),
    ...(isContract && certificatePaidBy
      ? { certificate_paid_by: certificatePaidBy }
      : {}),
    ...(isContract && surchargePayment > 0
      ? { surcharge_fee: surchargePayment.toFixed(2) }
      : {}),
    covid_19: covid19,
    notes: transactionNotes.trim(),
    transaction_notes: transactionNotes.trim(),
  }
}
